// ts-rest contract for the sidebar count badges (SEP-210, widened by SEP-208).
//
// The sidebar shows a red count on Overdrawn Cash, Corporate Actions and the
// Onboarding row. The sidebar is always mounted, so it must not pull the three
// full list payloads to count them — it asks for the three numbers instead.
// This is the same reason `review-queue/counts` exists beside the full
// review-queue model (PRD #156).
//
// Overdrawn Cash and Corporate Actions are Desk rows and report zero to any
// other persona. The Onboarding row belongs to all three pipeline teams: SEP-208
// found that a case reaching the Ops-owned Account opening stage told Ops
// nothing, because only the Desk sidebar carried the badge.
//
// Every count comes from the caller's own `ctx`, so the scope is the identity's.
// There is no user, IM or team parameter, and there can never be one.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

/**
 * The badged sidebar rows that carry a per-user "seen" stamp. Overdrawn Cash
 * and Corporate Actions need no stamp: their counts are outstanding work, which
 * clears when the IM does the work, not when they look at it. Onboarding is
 * different — a case arriving in your team's court is news, and news is read.
 */
export const navSeenKeySchema = z.enum(['onboarding']);
export type NavSeenKey = z.infer<typeof navSeenKeySchema>;

/**
 * Each count belongs to a feature. The key is absent when the Feature Manifest
 * turns that feature off (ADR-0027): the sidebar then has no row to badge.
 */
export const navCountsResponseSchema = z.object({
    /**
     * Overdrawn cash rows in the IM's book that are not yet resolved. Desk only.
     * `wealth.ops.overdrawn-cash.desk-alerts`.
     */
    overdrawnCash: z.number().int().nonnegative().optional(),
    /**
     * Corporate actions routed to this IM and still awaiting their election.
     * Desk only. `wealth.ops.corporate-actions.desk-elections`.
     */
    corporateActions: z.number().int().nonnegative().optional(),
    /**
     * Onboarding cases that arrived in the caller's team since they last opened
     * the board — Desk, Ops or Compliance, whichever the caller acts as.
     * `wealth.onboarding.pipeline`.
     */
    onboarding: z.number().int().nonnegative().optional(),
    /**
     * Peer reviews that Compliance assigned to the caller and that stay open
     * (SEP-329). Desk only, for an investment manager.
     * `wealth.onboarding.suitability.peer-review`.
     */
    peerReviews: z.number().int().nonnegative().optional(),
    /**
     * Cases that the IM sent to Compliance for the final approval and that
     * Compliance has not decided (SEP-334). Compliance only.
     * `dowgate.onboarding.method`.
     */
    finalApprovals: z.number().int().nonnegative().optional(),
});
export type NavCountsResponse = z.infer<typeof navCountsResponseSchema>;

export const navSeenRequestSchema = z.object({
    navKey: navSeenKeySchema,
});
export type NavSeenRequest = z.infer<typeof navSeenRequestSchema>;

export const navContract = c.router(
    {
        counts: {
            method: 'GET',
            path: '/nav/counts',
            responses: {
                200: navCountsResponseSchema,
            },
            summary: 'Outstanding-work counts for the sidebar badges, scoped to the caller',
        },
        markSeen: {
            method: 'POST',
            path: '/nav/seen',
            body: navSeenRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
            },
            summary: 'Stamp that the caller has looked at a badged sidebar row',
        },
    },
    { pathPrefix: '/api' },
);
